/* eslint-disable react/jsx-key */
import { List } from '@/components/List/List';
import { type ReactElement, useState, useEffect } from 'react';
import { Select, Text, Input } from '@mantine/core';
import { getFetcher } from '@/api/fetchers';
import { useNavigate } from 'react-router-dom';
import { createKey, readKey } from '@/api/params';
import { BooleanBadge } from '@/components/Badge/BooleanBadge';
import { TextHeader } from '@/components/List/Header/TextHeader';
import { AlignedCell } from '@/components/List/Cell/AlignedCell';
import { ClampedTextCell } from '@/components/List/Cell/ClampedTextCell';
import useSWR from 'swr';
import { MappingToComboBoxItem } from '@/schemas/converters/mappingShemasConverter';
import { type Services } from '@/schemas/API/GET/service/response';
import type { UsersRequest } from '@/schemas/API/GET/users/request';
import type { User, Users } from '@/schemas/API/GET/users/response';
import { toNumber } from 'lodash';
import { type Mapping } from '@/schemas/API/GET/mapping/response';
import { SortHeader } from '@/components/List/Header/SortHeader';

const UserNameCell = (user: User) => (
  <ClampedTextCell>
    {user.firstName} {user.lastName}
  </ClampedTextCell>
);

const UserEmailCell = (user: User) => <Text>{user.email}</Text>;

const UserRolesMapCell = (rolesMap?: Record<string, string> | undefined) => {
  const Cell = (user: User) => <Text>{rolesMap?.[user.role]}</Text>;
  return Cell;
};

const UserAccessCell = (user: User) => (
  <AlignedCell align="center">
    <BooleanBadge
      failLabel="Zablokován"
      successLabel="Povolen"
      successCond={() => !user.blocked}
    />
  </AlignedCell>
);

const UserServicesCell = (availableServices: Services) => {
  const Cell = (user: User) => (
    <ClampedTextCell>
      {user.services
        .map(
          serviceId =>
            availableServices?.find(service => service.id === serviceId)?.name
        )
        .filter(Boolean)
        .join(', ')}
    </ClampedTextCell>
  );
  return Cell;
};

export const UserListPage = (): ReactElement => {
  const navigate = useNavigate();
  const [requestParams, setRequestParams] = useState<UsersRequest>({
    page: 1,
    sort: [],
    size: 10,
  });

  useEffect(() => {
    const queryParams = readKey(window.location.search);
    setRequestParams(currentParams => ({
      ...currentParams,
      ...queryParams,
    }));
  }, []);

  const { data: availableServices } = useSWR<Services>('services', getFetcher);
  const { data: rolesMap } = useSWR<Mapping>('mappings/roles', getFetcher);
  const { data: users } = useSWR<Users>(
    '/users' + createKey(requestParams),
    getFetcher
  );

  useEffect(() => {
    navigate(createKey(requestParams), { replace: true });
  }, [requestParams, navigate]);

  const setRequestParam = <K extends keyof UsersRequest>(
    fieldName: K,
    value: UsersRequest[K] | null
  ): void => {
    setRequestParams(prevParams => {
      return { ...prevParams, [fieldName]: value };
    });
  };

  const SearchBar = (
    <Input.Wrapper label="Vyhledat">
      <Input
        placeholder="např: Václav"
        value={requestParams?.query}
        onChange={e => setRequestParam('query', e.currentTarget.value)}
      />
    </Input.Wrapper>
  );

  const RoleSelector = (
    <Select
      label="Role"
      value={
        requestParams.role && rolesMap
          ? rolesMap[requestParams.role]
          : undefined
      }
      onChange={selected => setRequestParam('role', selected ?? null)}
      data={rolesMap ? MappingToComboBoxItem(rolesMap) : []}
      clearable
    />
  );

  const AccsesSelector = (
    <Select
      placeholder="Všechny"
      label="Přístup"
      value={
        requestParams.block === null
          ? null
          : String(toNumber(requestParams.block))
      }
      clearable
      onChange={e =>
        setRequestParam('block', e === null ? null : Boolean(toNumber(e)))
      }
      data={[
        {
          value: '0',
          label: 'Povolen',
        },
        {
          value: '1',
          label: 'Zablokován',
        },
      ]}
    />
  );

  const ServiceSelector = (
    <Select
      label="Služby"
      placeholder="Všechny"
      clearable
      value={String(requestParams.serv)}
      onChange={e => setRequestParam('serv', e ? Number(e) : null)}
      data={availableServices?.map(service => ({
        value: service.id.toString(),
        label: service.name,
      }))}
    />
  );

  return (
    <div className="h-full p-8 overflow-auto">
      <List
        name="Uživatelé"
        pagination={{
          page: Number(requestParams.page),
          setPage: e => setRequestParam('page', e),
          pageTotal: users?.totalPages ?? 1,
        }}
        loading={!availableServices || !users}
        perPageSelector={{
          label: 'Uživatelů na stránce',
          value: requestParams.size ?? 0,
          setter: v => setRequestParam('size', v),
        }}
        selectors={[SearchBar, RoleSelector, AccsesSelector, ServiceSelector]}
        columns={[
          <SortHeader
            label="Příjmení a jméno"
            fields={['firstName', 'lastName']}
            callback={sort => setRequestParam('sort', sort)}
            currentSort={requestParams.sort ?? []}
          />,
          <TextHeader>E-mail</TextHeader>,
          <TextHeader>Role</TextHeader>,
          <TextHeader align="center">Přístup </TextHeader>,
          <TextHeader>Služby</TextHeader>,
        ]}
        content={{
          items: users?.content ?? [],
          onClick: (user: User) => navigate(`/users/${user.id}/info`),
          renders: [
            UserNameCell,
            UserEmailCell, 
            UserRolesMapCell(rolesMap),
            UserAccessCell,
            UserServicesCell(availableServices ?? []),
          ],
        }}
      />
    </div>
  );
};
